import { z } from "zod";

export const DEFAULT_AGENT_LINE = "default";

export const agentLineNameSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9-]*$/u);

export const agentLineMigrationIdSchema = z
    .string()
    .regex(/^\d{4}_[a-z0-9]+(?:_[a-z0-9]+)*$/u);

export const agentSubjectEmailSchema = z
    .email()
    .max(320)
    .refine((value) => value === value.trim().toLowerCase(), {
        message: "Subject emails must be lowercase",
    });

export const agentSubjectRoleSchema = z.enum(["admin"]);

export const agentLineCohortSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9-]*$/u);

export const agentSubjectSchema = z
    .object({
        email: agentSubjectEmailSchema.optional(),
        roles: z.array(agentSubjectRoleSchema).max(8).optional(),
        cohorts: z.array(agentLineCohortSchema).max(64).optional(),
    })
    .strict();

export const agentLineSelectorSchema = z.union([
    z
        .object({ emails: z.array(agentSubjectEmailSchema).min(1).max(256) })
        .strict(),
    z.object({ role: agentSubjectRoleSchema }).strict(),
    z.object({ cohort: agentLineCohortSchema }).strict(),
    z
        .object({
            percent: z.int().min(1).max(99),
            key: z.literal("email"),
            salt: z.string().min(1).max(64).optional(),
        })
        .strict(),
]);

export const agentLineMigrationSchema = z
    .object({
        id: agentLineMigrationIdSchema,
        from: z.object({ line: agentLineNameSchema }).strict(),
        to: z
            .object({
                line: agentLineNameSchema,
                version: z.literal("latest").optional(),
            })
            .strict(),
        who: agentLineSelectorSchema.optional(),
        script: z.literal(true).optional(),
        engineImport: z.literal(true).optional(),
    })
    .strict()
    .superRefine((migration, context) => {
        if (migration.from.line === migration.to.line) {
            context.addIssue({
                code: "custom",
                message: "A line migration must move to a different line",
                path: ["to", "line"],
            });
        }
        if (migration.engineImport === true && migration.script !== true) {
            context.addIssue({
                code: "custom",
                message: "Engine import requires a migration script",
                path: ["engineImport"],
            });
        }
    });

export const agentLineMigrationLogSchema = z
    .array(agentLineMigrationSchema)
    .superRefine((migrations, context) => {
        for (const [index, migration] of migrations.entries()) {
            const previous = migrations[index - 1];
            if (previous === undefined) continue;
            if (migration.id <= previous.id) {
                context.addIssue({
                    code: "custom",
                    path: [index, "id"],
                    message: "Line migration ids must be unique and ascending",
                });
            }
        }
    });

export const agentLineMigrationsDefinitionSchema = z
    .object({
        agentId: z.string().min(1).max(256),
        migrations: agentLineMigrationLogSchema,
    })
    .strict();

export type AgentSubject = z.infer<typeof agentSubjectSchema>;
export type AgentLineSelector = z.infer<typeof agentLineSelectorSchema>;
export type AgentLineMigration = z.infer<typeof agentLineMigrationSchema>;
export type AgentLineMigrationLog = z.infer<typeof agentLineMigrationLogSchema>;
export type AgentLineMigrationsDefinition = z.infer<
    typeof agentLineMigrationsDefinitionSchema
>;
